import {DownloadOutlined, FileTextOutlined} from '@ant-design/icons';
import {Button, Modal} from 'antd';
import dayjs from 'dayjs';
import html2pdf from 'html2pdf.js';
import PropTypes from 'prop-types';
import {useRef} from 'react';

const InventoryLotInvoice = ({open, lot, onClose}) => {
  const invoiceRef = useRef(null);

  if (!lot) {return null;}

  const {
    lotNumber,
    supplierName,
    warehouse,
    purchaseDate,
    manufactureDate,
    expiryDate,
    bill = {},
    products = [],
  } = lot;

  const {
    invoiceNumber,
    supplierInvoice,
    subtotal  = 0,
    grandTotal = 0,
  } = bill;

  const handleDownloadPDF = () => {
    html2pdf()
      .set({
        margin:      0.5,
        filename:    `LotInvoice-${lotNumber || invoiceNumber || 'lot'}.pdf`,
        image:       {type: 'jpeg', quality: 0.98},
        html2canvas: {scale: 2},
        jsPDF:       {unit: 'mm', format: 'a4', orientation: 'landscape'},
      })
      .from(invoiceRef.current)
      .save();
  };

  const fmtDate = ts => {
    if (!ts) {return '—';}
    return dayjs(ts).format('DD-MMM-YYYY');
  };

  const fmtCurrency = val =>
    val != null && val !== '' ? `₹${Number(val).toFixed(2)}` : '—';

  return (
    <Modal
      open={open}
      onCancel={onClose}
      destroyOnClose
      width={1050}
      title={(
        <div className={'flex items-center gap-2'}>
          <FileTextOutlined className={'text-blue-600'} />
          <span className={'font-bold text-gray-800 text-base'}>
            Lot Invoice —{' '}
            <span className={'font-mono text-blue-600'}>{invoiceNumber || lotNumber || '—'}</span>
          </span>
        </div>
      )}
      styles={{body: {maxHeight: '80vh', overflowY: 'auto', padding: '16px 24px'}}}
      footer={(
        <div className={'flex justify-end gap-3 py-1'}>
          <Button className={'btn-secondary'} onClick={onClose}>Close</Button>
          <Button
            icon={<DownloadOutlined />}
            onClick={handleDownloadPDF}
            type={'primary'}
            style={{background: '#3a5f98'}}
          >
            Download PDF
          </Button>
        </div>
      )}
    >
      {/* ── Invoice Preview ── */}
      <div
        ref={invoiceRef}
        className={'bg-white text-[12px] font-medium p-3'}
        style={{width: '100%', minHeight: '180mm'}}
      >
        <h1 className={'text-3xl font-bold text-center mb-2'}>LOT PURCHASE INVOICE</h1>

        {/* Date + Invoice Number row */}
        <div className={'grid grid-cols-2 mb-2'}>
          <div className={'p-2 font-semibold'}>
            Purchase Date: {fmtDate(purchaseDate || Date.now())}
          </div>
          <div className={'p-2 font-semibold text-right'}>
            Invoice #: {invoiceNumber || '—'}
          </div>
        </div>

        {/* Company info + Supplier info */}
        <div className={'grid grid-cols-2 border border-primary-300 mb-4'}>
          {/* Left: Company */}
          <div className={'p-2 border-r border-primary-300'}>
            <h2 className={'font-bold uppercase text-base'}>Samadhan Industries</h2>
            <p className={'mb-0 font-bold'}>Address:</p>
            <span className={'font-semibold'}>Plot No.-F-1, MIDC, Ishwarpur.</span>
            <p className={'mb-0'}>Tal.- Walwa, Dist.-Sangali</p>
            <p className={'mb-0 mt-1'}><b>State Code:</b> MH(27)</p>
            <p className={'mb-0'}><b>Email:</b> samadhanindustriesgroup89@gmail.com</p>
            <p className={'mb-0'}><b>Factory / OFF:</b> 8412912712 / 8485824712</p>
          </div>

          {/* Right: Supplier details */}
          <div className={'p-2'}>
            <p className={'font-bold text-base mb-1'}>Supplier Details</p>
            <p className={'mb-0'}>
              <b>Supplier Name:</b> {supplierName || '—'}
            </p>
            <p className={'mb-0'}>
              <b>Warehouse:</b> {warehouse || '—'}
            </p>
            {supplierInvoice && (
              <p className={'mb-0'}>
                <b>Supplier Invoice #:</b>{' '}
                <span className={'font-mono'}>{supplierInvoice}</span>
              </p>
            )}
            {lotNumber && (
              <p className={'mb-0'}>
                <b>Lot #:</b>{' '}
                <span className={'font-mono'}>{lotNumber}</span>
              </p>
            )}
            {manufactureDate && (
              <p className={'mb-0'}><b>Mfg Date:</b> {fmtDate(manufactureDate)}</p>
            )}
            {expiryDate && (
              <p className={'mb-0'}><b>Expiry Date:</b> {fmtDate(expiryDate)}</p>
            )}
          </div>
        </div>

        {/* Products table */}
        <table className={'border-collapse border border-primary-300 w-full text-sm'}>
          <thead>
            <tr className={'bg-gray-200 font-bold'}>
              <th className={'border border-primary-300 p-1'}>Sr</th>
              <th className={'border border-primary-300 p-1'}>Product Name</th>
              <th className={'border border-primary-300 p-1'}>HSN Code</th>
              <th className={'border border-primary-300 p-1'}>Qty</th>
              <th className={'border border-primary-300 p-1'}>Unit</th>
              <th className={'border border-primary-300 p-1'}>Purchase Price</th>
            </tr>
          </thead>

          <tbody>
            {products.length === 0 && (
              <tr>
                <td colSpan={6} className={'text-center p-3 text-gray-400'}>
                  No products in this lot.
                </td>
              </tr>
            )}
            {products.map((item, idx) => (
              <tr key={item.productId || idx}>
                <td className={'border border-primary-300 text-center p-1'}>{idx + 1}</td>
                <td className={'border border-primary-300 p-1'}>
                  {item.productName || '—'}
                </td>
                <td className={'border border-primary-300 text-center p-1'}>
                  {item.hsnCode || '—'}
                </td>
                <td className={'border border-primary-300 text-center p-1'}>
                  {item.quantity ?? '—'}
                </td>
                <td className={'border border-primary-300 text-center p-1'}>
                  {item.unit || '—'}
                </td>
                <td className={'border border-primary-300 text-center p-1'}>
                  {fmtCurrency(item.purchasePrice)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Tax + Totals section — values shown as-is from payload, no recalculation */}
        <div className={'flex w-full justify-between border border-primary-300 mt-3'}>
          {/* Left: tax rates */}
          <div className={'p-3 font-semibold text-primary-800 text-sm'}>
           
          </div>

          {/* Right: subtotal + grand total as received from payload */}
          <div className={'p-3 text-right'}>
            {subtotal > 0 && (
              <p className={'text-base font-semibold text-gray-700 mb-1'}>
                Subtotal: {fmtCurrency(subtotal)}
              </p>
            )}
            <p className={'text-xl font-bold mt-1'}>
              Grand Total: {fmtCurrency(grandTotal)}
            </p>
          </div>
        </div>
      </div>

      <p className={'text-center text-gray-400 font-medium text-base mt-3'}>
        <i>- Inventory Purchase Record -</i>
      </p>
    </Modal>
  );
};

InventoryLotInvoice.propTypes = {
  open:    PropTypes.bool.isRequired,
  lot:     PropTypes.object,
  onClose: PropTypes.func.isRequired,
};

export default InventoryLotInvoice;
